import { useState } from 'react';

import { useToaster } from './useToaster';

import { SupportFormData } from '@/components/supportForm';
import { sendSupportRequest, SupportRequestData } from '@/services/sendSupportRequest';
import { isApiErrorV2 } from '@/util/fetchClient';

export function useSupportForm(
  options: {
    initialValues?: Partial<SupportFormData>;
    userData?: {
      npi?: string;
      institution?: string;
      state?: string;
      firstName?: string;
      lastName?: string;
      identifiesAsDoctor?: boolean;
    };
  } = {}
) {
  const [isOpen, setIsOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const toast = useToaster();

  const handleSubmit = async (data: SupportFormData) => {
    setIsLoading(true);

    const requestData: SupportRequestData = {
      email: data.email,
      reason: data.reason,
      message: data.message,
      name: data.name,
      user: options.userData
        ? {
            npiNumber: options.userData.npi ?? null,
            institution: options.userData.institution ?? null,
            state: options.userData.state ?? null,
            firstName: options.userData.firstName ?? '',
            lastName: options.userData.lastName ?? '',
            identifiesAsDoctor: options.userData.identifiesAsDoctor ?? false,
          }
        : undefined,
    };

    try {
      await sendSupportRequest(requestData);

      toast.showSuccess({
        title: 'We got your request',
        message: 'Thank you for reaching out. We will be in touch soon!',
      });

      setIsOpen(false);
    } catch (error) {
      const message = (error as any)?.body?.error?.issues?.[0]?.message;

      const zodMessage = typeof message === 'string' ? message : undefined;

      toast.showError({
        title: 'Could not submit request',
        ...(isApiErrorV2(error)
          ? {
              apiError: {
                error,
                errorPatternMatcher: {},
              },
            }
          : {
              message: zodMessage ?? 'Please try again.',
            }),
      });

      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isOpen,
    onOpen: () => setIsOpen(true),
    onClose: () => setIsOpen(false),
    onSubmit: handleSubmit,
    initialValues: options.initialValues,
    isLoading,
  };
}
